
import React, { useState, useEffect } from "react";
import nl2br from "react-nl2br";
import Input from "@mui/joy/Input";
import Tooltip from "@mui/joy/Tooltip";
import Textarea from "@mui/joy/Textarea";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import IconButton from "@mui/joy/IconButton";
import { getIcon } from "utils/misc";
import { api_post } from "api/utils";
import { Success, Error } from 'components/FancyToast';
import useUserIdentity from "hooks/useUserIdentity";

import { JoyTypeLevels } from '../types';

type UpdateableFieldProps = {
    value: string,
    type: string,
    field: string,
    placeholder: string,
    uuid?: string,
    multiline?: boolean,
    level?: JoyTypeLevels,
    startDecorator?: React.ReactNode,
    editable?: boolean,
};

// Component accept text, placeholder values and also pass what type of Input - input, textarea so that we can use it for styling accordingly
const UpdateableField = ({
    value,
    placeholder,
    type,
    field,
    uuid,
    multiline,
    level,
    startDecorator,
    editable = true,
    ...props
}: UpdateableFieldProps) => {
    // Manage the state whether to show the label or the input box. By default, label will be shown.
    // Exercise: It can be made dynamic by accepting initial state as props outside the component
    const [isEditing, setEditing] = useState(false);
    const SaveIcon = getIcon("Check");
    const CancelIcon = getIcon("CancelOutlined");
    const { currentIdentity } = useUserIdentity();
    const [ourValue, setOurValue] = useState(value);
    const [initialValue, setInitialValue] = useState(value);


    useEffect(() => {
        setOurValue(value);
        setInitialValue(value);
    }, [value]);

    // Save if they type enter.
    // Stop if they type escape.
    const onKeyUp = (e: string) => {
        if (e === "Enter") {
            handleSave();
        }
        else if (e === "Escape") {
            setEditing(false);
        }
    }

    // Event handler while pressing any key while editing
    const onChange = (v: string) => {
            setOurValue(v);
    };

    const handleSave = () => {
        const data = {
            field: field,
            value: ourValue,
            identity: currentIdentity.uuid,
            [type]: uuid ? uuid : "",
        };

        api_post(`/${type}/update-field`, data)
            .then((response: Response) => {
                if (response.ok) {
                    Success("Saved");
                    // the saved value is the new initial value
                    setInitialValue(ourValue);
                    console.log("saved: %o", response);
                }
                else {
                    Error("Unable to save: " + response.statusText + " (" + response.status + ")");

                }
            })
            .catch((err: object) => {
                Error("Unable to save");
                console.log("error: %o", err);
            })
            .finally(() => {
                setEditing(false);
            });

    };
    return (
        <section {...props}>
            {isEditing ? (
                <Stack direction="row" spacing={1}>
                    {multiline ?
                        <Textarea
                            startDecorator={startDecorator}
                            onChange={e => onChange(e.target.value)}
                            // html input attribute
                            name="title"
                            minRows={2}
                            value={ourValue}
                            placeholder={placeholder}
                             sx={{ width: "100%" }}
                              endDecorator={
                                  <Stack
                                      direction="row"
                                      sx={{ width: "100%" }}
                                      justifyContent="flex-end"
                                  >

                                  <Stack
                                      direction="row-reverse"
                                      alignItems="center"
                                      justifyContent="flex-end"
                                      spacing={1}>

                                    <Tooltip arrow title="Save this now">
                                        <IconButton
                                            onClick={handleSave} >
                                            <SaveIcon color="success"

                                            />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip arrow title="Cancel">
                                        <IconButton
                                            onClick={() => { setOurValue(initialValue); setEditing(false) }} >
                                            <CancelIcon color="neutral" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                  </Stack>
                                      }

                        />
                        : <Input
                            startDecorator={startDecorator}
                              onKeyUp={e => onKeyUp(e.key)}
                            onChange={e => onChange(e.target.value)}
                            // html input attribute
                            name="title"
                              type="text"
                             sx={{ width: "100%" }}
                            placeholder={placeholder}
                            value={ourValue}
                              endDecorator={
                                <Stack direction="row-reverse" alignItems="center" justifyContent="flex-end" spacing={1}>

                                    <Tooltip arrow title="Save this now">
                                        <IconButton
                                            onClick={handleSave} >
                                            <SaveIcon color="success"

                                            />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip arrow title="Cancel">
                                        <IconButton
                                            onClick={() => { setOurValue(initialValue); setEditing(false) }} >
                                            <CancelIcon color="neutral" />
                                        </IconButton>
                                    </Tooltip>

                                </Stack>
                              }
                        />
                    }


                </Stack>


            ) : (

                <Typography
                    onClick={() => editable && setEditing(true)}
                    startDecorator={startDecorator}
                    level={level} color={ourValue ? undefined : "neutral"}>

                    {(multiline ? nl2br(ourValue) : ourValue) || placeholder}
                </Typography>

            )}
        </section>
    );
};

export default UpdateableField;
